import React, { useState } from 'react';
import { Avatar, CircularProgress, Box, SxProps, Theme } from '@mui/material';
import { useWorkspaceAvatar } from '../../../../hooks/useWorkspaceAvatar';

interface WorkspaceAvatarProps {
  volumeName: string;
  shortName: string;
  size?: number;
  fontSize?: string;
  sx?: SxProps<Theme>;
  className?: string;
}

const WorkspaceAvatar = ({
  volumeName,
  shortName,
  size = 40,
  fontSize = '0.9rem',
  sx = {},
  className
}: WorkspaceAvatarProps) => {
  const { logoUrl, initials, backgroundColor, handleImageError, showImage } = useWorkspaceAvatar({
    volumeName,
    shortName
  });
  
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleError = () => {
    setImageLoading(false);
    handleImageError();
  };

  return (
    <Avatar
      className={className}
      sx={{
        width: size,
        height: size,
        fontSize,
        fontWeight: 600,
        backgroundColor: showImage ? 'transparent' : backgroundColor,
        border: (theme) => `2px solid ${theme.palette.common.white}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        flexShrink: 0,
        position: 'relative',
        ...sx,
      }}
    >
      {showImage ? (
        <>
          {imageLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '50%',
              }}
            >
              <CircularProgress 
                size={size * 0.5} 
                thickness={4} 
                sx={{ color: 'primary.main' }} 
              />
            </Box>
          )}
          <img
            src={logoUrl}
            alt={shortName}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: imageLoading ? 0 : 1,
              transition: 'opacity 0.3s ease',
            }}
            onLoad={handleImageLoad}
            onError={handleError}
          />
        </>
      ) : (
        <Box component="span" sx={{ fontSize, fontWeight: 600, color: 'common.white' }}>
          {initials}
        </Box>
      )}
    </Avatar>
  );
};

export default WorkspaceAvatar;
